<template>
  <GenericComments :lesson-id="lessonId" :comment-service="commentService" />
</template>

<script setup>
import { defineProps } from "vue";
import GenericComments from "../common/GenericComments.vue";
import { createLessonCommentService } from "@/services/commentServiceFactory";

defineProps({
  lessonId: {
    type: [String, Number],
    required: true,
  },
});

const commentService = createLessonCommentService();
</script>

<style scoped>
.lesson-comments {
  margin: 0 auto;
}

.lesson-comments h2 {
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: #333;
}

/* Comments list styles */
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
}

.comment-item {
  background-color: #f8f9fa;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #e0e0e0;
  position: relative;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-blue, #007bff);
  color: white;
  font-weight: bold;
}

.username {
  font-weight: 600;
  color: #333;
}

.comment-date {
  font-size: 0.8rem;
  color: #777;
}

.comment-content {
  font-size: 1rem;
  color: #444;
  line-height: 1.5;
  white-space: pre-line;
  word-break: break-word;
}

.comment-footer {
  display: flex;
  margin-top: 16px;
  align-items: center;
}

.like-button {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 6px 10px;
  border-radius: 20px;
  cursor: pointer;
  color: #666;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.like-button:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.like-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.like-button.liked {
  color: var(--primary-pink, #ff5a5f);
}

.like-icon {
  width: 18px;
  height: 18px;
}

.like-loading {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.comment-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.edit-btn {
  color: var(--primary-blue, #007bff);
}

.delete-btn {
  color: var(--primary-pink, #ff5a5f);
}

/* Comment form styles */
.lesson-comments-form-container {
  width: 100%;
  border: 0.2rem solid #d8dae6;
  border-radius: 16px;
  padding: 20px;
  margin-top: 40px;
  background-color: white;
}

.lesson-comments-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lesson-comments-form textarea {
  width: 100%;
  padding: 12px 15px;
  border-radius: 8px;
  border-bottom: 0.2rem solid #d8dae6;
  resize: vertical;
  font-family: inherit;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.lesson-comments-form textarea:focus {
  outline: none;
  border-color: var(--primary-blue, #007bff);
}

.lesson-comments-form-button {
  width: 100px;
  align-self: flex-end;
  margin-top: 10px;
  border-radius: 8px;
}

/* Edit form styles */
.comment-edit-form {
  margin-top: 10px;
}

.comment-edit-form textarea {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  border: 0.2rem solid #d8dae6;
  font-size: 1rem;
}

.comment-edit-form textarea:focus {
  outline: none;
  border-color: var(--primary-blue, #007bff);
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

/* Loading and error states */
.comments-loading,
.comments-error,
.no-comments {
  padding: 40px 20px;
  text-align: center;
  border-radius: 8px;
  background-color: #f8f9fa;
  margin-top: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--primary-blue, #007bff);
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.comments-error {
  color: var(--primary-pink, #ff5a5f);
}

.auth-warning {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

/* Delete confirmation modal */
.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.delete-modal {
  background-color: white;
  border-radius: 16px;
  padding: 30px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.delete-modal h3 {
  color: var(--primary-pink, #ff5a5f);
  margin-top: 0;
  margin-bottom: 15px;
}

.delete-modal p {
  margin-bottom: 25px;
  color: #444;
}

.delete-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .lesson-comments {
    width: 100%;
  }

  .lesson-comments-form-button {
    width: 100%;
  }

  .delete-modal {
    padding: 20px;
  }
}
</style>
